import { useState } from 'react'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import { Search, Download } from 'lucide-react'
import { mockAbroadLeads, mockAdminMentors, type AbroadLead } from '@/data/admin-mock-data'
import { ExportModal, type ExportFilters } from './export-modal'

const statusColors: Record<AbroadLead['status'], string> = {
    new: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
    contacted: 'bg-slate-100 dark:bg-slate-800 text-theme-secondary',
    counselling: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
    applied: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300',
    admitted: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
    closed: 'bg-slate-200 dark:bg-slate-700 text-theme-muted',
}

export function AdminAbroadLeadsView() {
    const [leads, setLeads] = useState(mockAbroadLeads)
    const [searchTerm, setSearchTerm] = useState('')
    const [statusFilter, setStatusFilter] = useState<string>('all')
    const [selectedLead, setSelectedLead] = useState<AbroadLead | null>(null)
    const [showExportModal, setShowExportModal] = useState(false)

    const filtered = leads.filter(l => {
        const matchSearch = !searchTerm || l.studentName.toLowerCase().includes(searchTerm.toLowerCase()) || l.collegeName.toLowerCase().includes(searchTerm.toLowerCase())
        const matchStatus = statusFilter === 'all' || l.status === statusFilter
        return matchSearch && matchStatus
    })

    const assignMentor = (leadId: string, mentorId: string) => {
        const mentor = mockAdminMentors.find(m => m.id === mentorId)
        setLeads(prev => prev.map(l => l.id === leadId ? { ...l, assignedMentorId: mentorId, assignedMentorName: mentor?.name ?? null } : l))
    }

    const updateStatus = (leadId: string, status: AbroadLead['status']) => {
        setLeads(prev => prev.map(l => l.id === leadId ? { ...l, status } : l))
    }

    const handleExport = (filters: ExportFilters) => {
        let exportData = [...leads]

        // Filter by Date
        if (filters.dateRange !== 'all') {
            const start = new Date(filters.startDate)
            const end = new Date(filters.endDate)
            end.setHours(23, 59, 59, 999)

            exportData = exportData.filter(l => {
                const date = new Date(l.createdAt)
                return date >= start && date <= end
            })
        }

        // Filter by Entity (College)
        if (filters.entityId !== 'all') {
            exportData = exportData.filter(l => l.collegeId === filters.entityId)
        }

        const headers = ['Student Name', 'Email', 'Phone', 'College', 'Status', 'Assigned Mentor', 'Date']
        const csvContent = [
            headers.join(','),
            ...exportData.map(l => [
                `"${l.studentName}"`,
                `"${l.email}"`,
                `"${l.phone}"`,
                `"${l.collegeName}"`,
                `"${l.status}"`,
                `"${l.assignedMentorName || 'Unassigned'}"`,
                `"${l.createdAt}"`
            ].join(','))
        ].join('\n')

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob)
            link.setAttribute('href', url)
            link.setAttribute('download', `abroad_leads_${new Date().toISOString().split('T')[0]}.csv`)
            link.style.visibility = 'hidden'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        }
    }

    const uniqueColleges = Array.from(new Set(mockAbroadLeads.map(l => l.collegeId))).map(id => {
        const lead = mockAbroadLeads.find(l => l.collegeId === id)
        return { id, name: lead?.collegeName || 'Unknown' }
    })

    return (
        <div className="space-y-6 pb-24 lg:pb-0">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-theme-primary">Abroad Lead Assignments</h2>
                    <p className="text-sm text-theme-secondary">Leads from Foreign Education page. Assign counsellor/mentor and track status.</p>
                </div>
                <button
                    onClick={() => setShowExportModal(true)}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                >
                    <Download className="w-4 h-4" />
                    Export CSV
                </button>
            </div>

            <ExportModal
                isOpen={showExportModal}
                onClose={() => setShowExportModal(false)}
                onExport={handleExport}
                type="abroad"
                entities={uniqueColleges}
            />

            <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-theme-secondary" />
                    <input
                        type="text"
                        placeholder="Search by student or college..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary"
                    />
                </div>
                <select
                    value={statusFilter}
                    onChange={e => setStatusFilter(e.target.value)}
                    className="px-4 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary"
                >
                    <option value="all">All statuses</option>
                    {(['new', 'contacted', 'counselling', 'applied', 'admitted', 'closed'] as const).map(s => (
                        <option key={s} value={s}>{s}</option>
                    ))}
                </select>
            </div>

            {/* Desktop Table View */}
            <GlassmorphicCard className="hidden md:block overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                                <th className="p-4 text-sm font-semibold text-theme-primary">Student</th>
                                <th className="p-4 text-sm font-semibold text-theme-primary">College & Message</th>
                                <th className="p-4 text-sm font-semibold text-theme-primary">Status</th>
                                <th className="p-4 text-sm font-semibold text-theme-primary">Assigned To</th>
                                <th className="p-4 text-sm font-semibold text-theme-primary">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((l) => (
                                <tr key={l.id} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/30">
                                    <td className="p-4">
                                        <p className="font-medium text-theme-primary">{l.studentName}</p>
                                        <p className="text-xs text-theme-muted">{l.email} · {l.phone}</p>
                                    </td>
                                    <td className="p-4">
                                        <p className="text-theme-primary text-sm font-medium">{l.collegeName}</p>
                                        {l.message && <p className="text-xs text-theme-secondary mt-1 line-clamp-2" title={l.message}>{l.message}</p>}
                                    </td>
                                    <td className="p-4">
                                        <div className="relative">
                                            <select
                                                value={l.status}
                                                onChange={e => updateStatus(l.id, e.target.value as AbroadLead['status'])}
                                                className={`appearance-none text-sm font-medium px-3 py-1.5 rounded-full border-0 cursor-pointer pr-8 focus:ring-2 ring-primary-blue/20 ${statusColors[l.status]}`}
                                            >
                                                {(['new', 'contacted', 'counselling', 'applied', 'admitted', 'closed'] as const).map(s => (
                                                    <option key={s} value={s} className="bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100">
                                                        {s}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </td>
                                    <td className="p-4">
                                        <select
                                            value={l.assignedMentorId || ""}
                                            onChange={e => assignMentor(l.id, e.target.value)}
                                            className="text-sm rounded-lg bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary px-2 py-1 max-w-[150px]"
                                        >
                                            <option value="">Assign mentor...</option>
                                            {mockAdminMentors.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                                        </select>
                                    </td>
                                    <td className="p-4">
                                        <button
                                            onClick={() => setSelectedLead(l)}
                                            className="text-sm text-primary-blue hover:underline font-medium"
                                        >
                                            View
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </GlassmorphicCard>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-4">
                {filtered.map((l) => (
                    <GlassmorphicCard key={l.id} className="p-4">
                        <div className="flex justify-between items-start mb-3">
                            <div>
                                <h3 className="font-semibold text-theme-primary">{l.studentName}</h3>
                                <p className="text-xs text-theme-secondary">{l.collegeName}</p>
                            </div>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[l.status]}`}>
                                {l.status}
                            </span>
                        </div>

                        <div className="space-y-3 mb-4">
                            <div className="text-sm">
                                <p className="text-theme-muted text-xs mb-1">Contact</p>
                                <p className="text-theme-primary">{l.email}</p>
                                <p className="text-theme-primary">{l.phone}</p>
                            </div>

                            <div>
                                <p className="text-theme-muted text-xs mb-1">Status</p>
                                <select
                                    value={l.status}
                                    onChange={e => updateStatus(l.id, e.target.value as AbroadLead['status'])}
                                    className={`w-full text-sm p-2 rounded-lg border border-slate-200 dark:border-slate-700 ${statusColors[l.status]}`}
                                >
                                    {(['new', 'contacted', 'counselling', 'applied', 'admitted', 'closed'] as const).map(s => (
                                        <option key={s} value={s} className="bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100">
                                            {s}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <p className="text-theme-muted text-xs mb-1">Assigned Mentor</p>
                                <select
                                    value={l.assignedMentorId || ""}
                                    onChange={e => assignMentor(l.id, e.target.value)}
                                    className="w-full text-sm p-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary"
                                >
                                    <option value="">Assign mentor...</option>
                                    {mockAdminMentors.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                                </select>
                            </div>
                        </div>

                        <button
                            onClick={() => setSelectedLead(l)}
                            className="w-full py-2 bg-primary-blue/10 text-primary-blue rounded-lg text-sm font-medium"
                        >
                            View Details
                        </button>
                    </GlassmorphicCard>
                ))}
            </div>

            {/* Lead Details Modal */}
            {selectedLead && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setSelectedLead(null)}>
                    <GlassmorphicCard className="w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                        <div className="flex justify-between items-start mb-6">
                            <div>
                                <h3 className="text-xl font-bold text-theme-primary">{selectedLead.studentName}</h3>
                                <p className="text-sm text-theme-secondary">Abroad Education Lead</p>
                            </div>
                            <button onClick={() => setSelectedLead(null)} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors">
                                <span className="sr-only">Close</span>
                                <svg className="w-5 h-5 text-theme-secondary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                            </button>
                        </div>

                        <div className="space-y-6">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-xs font-semibold text-theme-muted uppercase mb-1">Email</p>
                                    <p className="text-theme-primary">{selectedLead.email}</p>
                                </div>
                                <div>
                                    <p className="text-xs font-semibold text-theme-muted uppercase mb-1">Phone</p>
                                    <p className="text-theme-primary">{selectedLead.phone}</p>
                                </div>
                            </div>

                            <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-xl space-y-3">
                                <div>
                                    <p className="text-xs font-semibold text-theme-muted uppercase mb-1">Interested College</p>
                                    <p className="text-theme-primary font-medium">{selectedLead.collegeName}</p>
                                </div>
                                {selectedLead.message && (
                                    <div>
                                        <p className="text-xs font-semibold text-theme-muted uppercase mb-1">Message from Student</p>
                                        <p className="text-theme-secondary text-sm italic bg-white dark:bg-slate-800 p-3 rounded-lg border border-slate-100 dark:border-slate-700">
                                            "{selectedLead.message}"
                                        </p>
                                    </div>
                                )}
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-xs font-semibold text-theme-muted uppercase mb-1">Current Status</p>
                                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${statusColors[selectedLead.status]}`}>
                                        {selectedLead.status}
                                    </span>
                                </div>
                                <div>
                                    <p className="text-xs font-semibold text-theme-muted uppercase mb-1">Assigned Counsellor</p>
                                    <p className="text-theme-primary">{selectedLead.assignedMentorName || 'Unassigned'}</p>
                                </div>
                            </div>

                            <div className="pt-4 border-t border-slate-200 dark:border-slate-700 flex justify-end">
                                <button
                                    onClick={() => setSelectedLead(null)}
                                    className="px-4 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-theme-primary rounded-lg font-medium transition-colors"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </GlassmorphicCard>
                </div>
            )}
        </div>
    )
}
